import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { http, errMsg, formatBytes } from "@/lib/api";
import { toast } from "sonner";
import {
    Camera, RefreshCw, Trash2, Play, Package, FileCog, Settings2,
    CheckCircle2, AlertCircle, Loader2, Monitor, ArrowRight,
    Download, FileText, Search, CheckSquare, Square, HardDrive, FolderOpen,
    AlertTriangle, FolderTree, X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { normalizeOs, osBadge, PROFILE_FOLDERS_BY_OS, CAPTURE_PHASES_BY_OS } from "@/lib/osMeta";
import FsBrowser from "@/components/FsBrowser";
import SearchInput, { filterByName } from "@/components/SearchInput";
import PageHero from "@/components/PageHero";
import RecentMigrationsPulse, { adaptCaptures } from "@/components/RecentMigrationsPulse";
import AppInventoryDrawer from "@/components/AppInventoryDrawer";

/**
 * Computer-to-Computer migration orchestrator UI.
 *
 * Three flows on one page:
 *   1. Start a new capture     — pick a source agent + which phases.
 *   2. Watch in-flight captures — phase-level live status (WS-driven).
 *   3. Review a completed capture and apply it to a destination agent.
 *
 * Backend is /api/migrations/* (see routers/migrations.py).
 */

const PHASE_META = {
    "scan-apps":        { label: "Scan installed apps",   icon: Package },
    "export-registry":  { label: "Export registry",       icon: FileCog },
    "export-settings":  { label: "Export settings",       icon: Settings2 },
    "estimate-profile": { label: "Estimate user profile", icon: HardDrive },
    "install-apps":     { label: "Install apps",          icon: Package },
    "import-registry":  { label: "Import registry",       icon: FileCog },
    "apply-settings":   { label: "Apply settings",        icon: Settings2 },
};

function PhaseBadge({ status }) {
    const styles = {
        // Phase vocabulary
        queued:        { label: "queued",        cls: "bg-zinc-700/50 text-zinc-300" },
        "in-progress": { label: "in progress",   cls: "bg-blue-600/30 text-blue-200" },
        completed:     { label: "completed",     cls: "bg-emerald-600/30 text-emerald-200" },
        failed:        { label: "failed",        cls: "bg-rose-600/30 text-rose-200" },
        // App-install vocabulary (apply drawer reuses this badge)
        pending:       { label: "pending",       cls: "bg-zinc-700/50 text-zinc-300" },
        installing:    { label: "installing",    cls: "bg-blue-600/30 text-blue-200" },
        installed:     { label: "installed",     cls: "bg-emerald-600/30 text-emerald-200" },
    };
    const s = styles[status] || styles.queued;
    return <span data-testid={`phase-status-${status}`} className={`px-2 py-0.5 rounded-full text-xs font-medium ${s.cls}`}>{s.label}</span>;
}

function StatusPill({ status }) {
    const styles = {
        capturing:  "bg-blue-600/30 text-blue-200 border-blue-500/40",
        applying:   "bg-blue-600/30 text-blue-200 border-blue-500/40",
        completed:  "bg-emerald-600/30 text-emerald-200 border-emerald-500/40",
        failed:     "bg-rose-600/30 text-rose-200 border-rose-500/40",
    };
    return (
        <span className={`px-2 py-1 rounded-md text-xs font-medium border ${styles[status] || "bg-zinc-700/50 text-zinc-200 border-zinc-600"}`}
            data-testid={`migration-status-${status}`}>
            {status}
        </span>
    );
}

/**
 * Compact step indicator for multi-step dialog flows. Renders dots + labels
 * with the active step highlighted and previous steps marked complete.
 * Pure presentation, no internal state.
 */
function StepIndicator({ steps, currentIndex, testIdPrefix = "step" }) {
    return (
        <div className="flex items-center gap-1.5 mb-1" data-testid={`${testIdPrefix}-indicator`}>
            {steps.map((label, i) => {
                const done = i < currentIndex;
                const active = i === currentIndex;
                return (
                    <React.Fragment key={label}>
                        <div className="flex items-center gap-1.5">
                            <div
                                className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-semibold transition-colors
                                    ${done ? "bg-emerald-500/30 text-emerald-300 border border-emerald-500/50"
                                      : active ? "bg-blue-500/30 text-blue-200 border border-blue-500/60"
                                      : "bg-zinc-800 text-zinc-500 border border-zinc-700"}`}
                                data-testid={`${testIdPrefix}-${i}-${active ? "active" : done ? "done" : "pending"}`}
                            >
                                {done ? <CheckCircle2 className="w-3 h-3" /> : i + 1}
                            </div>
                            <span className={`text-xs ${active ? "text-zinc-200 font-medium" : done ? "text-zinc-400" : "text-zinc-500"}`}>
                                {label}
                            </span>
                        </div>
                        {i < steps.length - 1 && (
                            <div className={`flex-1 h-px ${done ? "bg-emerald-500/40" : "bg-zinc-800"}`} />
                        )}
                    </React.Fragment>
                );
            })}
        </div>
    );
}

/**
 * Per-app install status drawer for an in-flight or recently-finished
 * apply. Polls /migrations/{captureId}/applies every 5 s, shows the most
 * recent apply's app_install_status map as one row per app, with a
 * "Retry failed" button that calls /apply/{id}/retry-apps.
 *
 * Collapsed by default — only expands when the operator clicks the
 * header. Most users never look at this; it's only critical when
 * 3 of 47 apps fail and you need to know which ones.
 */
function AppStatusDrawer({ captureId, captureStatus }) {
    const [applies, setApplies] = useState([]);
    const [open, setOpen] = useState(false);
    const [retrying, setRetrying] = useState(false);

    const fetchApplies = useCallback(async () => {
        try {
            const r = await http.get(`/migrations/${captureId}/applies`);
            setApplies(r.data || []);
        } catch {
            // Silent fail — capture may be too new to have applies.
        }
    }, [captureId]);

    useEffect(() => {
        if (captureStatus !== "completed") return;
        fetchApplies();
        // Poll every 5 s while drawer is open OR an apply is in-flight.
        const id = setInterval(fetchApplies, 5000);
        return () => clearInterval(id);
    }, [captureStatus, fetchApplies]);

    const latest = applies[0];
    if (!latest) return null;

    const statuses = latest.app_install_status || {};
    const statusEntries = Object.entries(statuses);
    const counts = statusEntries.reduce((acc, [, s]) => {
        acc[s] = (acc[s] || 0) + 1; return acc;
    }, {});
    const total = statusEntries.length;
    const installed = counts.installed || 0;
    const failed = counts.failed || 0;
    const installing = counts.installing || 0;
    // Whether the operator opted into the install-apps phase at apply
    // time. When false (registry-only or settings-only apply), we
    // suppress the "0/0 installed" counter — it's confusing because
    // there were no apps queued in the first place.
    const hasInstallAppsPhase = "install-apps" in (latest.phases || {});

    const retry = async () => {
        if (failed === 0) return;
        setRetrying(true);
        try {
            const r = await http.post(`/migrations/apply/${latest.id}/retry-apps`);
            toast.success(`Retry queued for ${r.data.queued} app(s)`);
            fetchApplies();
        } catch (e) {
            toast.error("Retry failed: " + errMsg(e));
        } finally {
            setRetrying(false);
        }
    };

    const headerCls = {
        applying:  "bg-blue-950/30 border-blue-800/40",
        completed: "bg-emerald-950/20 border-emerald-800/40",
        failed:    "bg-rose-950/30 border-rose-800/40",
    }[latest.status] || "bg-zinc-950/30 border-zinc-800";

    return (
        <div className="mt-4" data-testid={`apply-drawer-${captureId}`}>
            <button
                type="button"
                onClick={() => setOpen(o => !o)}
                className={`w-full flex items-center justify-between p-3 rounded-md border ${headerCls} hover:brightness-110 transition`}
                data-testid={`apply-drawer-toggle-${captureId}`}
            >
                <div className="flex items-center gap-2 text-sm">
                    <Play className="w-4 h-4 text-zinc-400" />
                    <span className="text-zinc-200 font-medium">Apply: {latest.destination_agent_name || "destination"}</span>
                    <StatusPill status={latest.status} />
                    <span className="text-xs text-zinc-500">
                        {hasInstallAppsPhase ? (
                            <>
                                {installed}/{total} installed
                                {installing > 0 && <span className="text-blue-300"> · {installing} installing</span>}
                                {failed > 0 && <span className="text-rose-300"> · {failed} failed</span>}
                            </>
                        ) : (
                            <span>registry / settings only</span>
                        )}
                    </span>
                </div>
                <span className="text-xs text-zinc-500">
                    {open ? "▼" : "▶"} {new Date(latest.created_at).toLocaleString()}
                </span>
            </button>

            {open && (
                <div className="mt-2 space-y-2" data-testid={`apply-drawer-body-${captureId}`}>
                    {failed > 0 && (
                        <div className="flex items-center justify-between p-2 rounded-md bg-rose-950/30 border border-rose-800/50">
                            <span className="text-xs text-rose-200">
                                {failed} app(s) failed. Re-runs winget install for failed entries only.
                            </span>
                            <Button
                                size="sm" variant="outline"
                                onClick={retry} disabled={retrying}
                                data-testid={`apply-drawer-retry-btn-${captureId}`}
                            >
                                {retrying ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                                Retry failed
                            </Button>
                        </div>
                    )}
                    <div className="rounded-md border border-zinc-800 max-h-72 overflow-y-auto">
                        {statusEntries.length === 0 ? (
                            <div className="p-3 text-xs text-zinc-500 text-center">No app statuses yet.</div>
                        ) : (
                            statusEntries
                                .sort(([, a], [, b]) => {
                                    // Failed first, then installing, then pending, then installed.
                                    const order = { failed: 0, installing: 1, pending: 2, installed: 3 };
                                    return (order[a] ?? 9) - (order[b] ?? 9);
                                })
                                .map(([wingetId, status]) => {
                                    const detail = latest.app_install_details?.[wingetId];
                                    const rowCls = {
                                        installing: "bg-blue-950/20 border-l-2 border-blue-500",
                                        installed:  "bg-emerald-950/15 border-l-2 border-emerald-500/60",
                                        failed:     "bg-rose-950/25 border-l-2 border-rose-500",
                                        pending:    "border-l-2 border-transparent",
                                    }[status] || "border-l-2 border-transparent";
                                    return (
                                        <div key={wingetId}
                                            className={`flex items-center justify-between gap-3 px-3 py-2 border-b border-zinc-800 last:border-0 ${rowCls}`}
                                            data-testid={`apply-app-row-${wingetId}`}>
                                            <div className="flex items-center gap-2 min-w-0">
                                                {status === "installing" ? <Loader2 className="w-3.5 h-3.5 text-blue-400 animate-spin shrink-0" />
                                                  : status === "installed" ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                                                  : status === "failed" ? <AlertCircle className="w-3.5 h-3.5 text-rose-400 shrink-0" />
                                                  : <Package className="w-3.5 h-3.5 text-zinc-500 shrink-0" />}
                                                <span className="font-mono text-xs text-zinc-300 truncate">{wingetId}</span>
                                                {detail?.detail && (
                                                    <span className="text-xs text-zinc-500 truncate">— {detail.detail}</span>
                                                )}
                                            </div>
                                            <PhaseBadge status={status} />
                                        </div>
                                    );
                                })
                        )}
                    </div>

                    {/* Phase 4: verification summary + checklist exports */}
                    <VerificationSummary applyId={latest.id} />
                </div>
            )}
        </div>
    );
}

// ---------------------------------------------------------------------------
// Phase 4: VerificationSummary — 8-state diff + Manual / Vendor / Full
// report CSV exports. Lazily fetches on mount of an apply drawer.
// ---------------------------------------------------------------------------
const VERIFICATION_LABEL = {
    verified:          "Verified",
    version_mismatch:  "Version mismatch",
    name_mismatch:     "Name mismatch",
    failed_install:    "Failed install",
    manual_required:   "Manual required",
    vendor_skipped:    "Vendor reinstall",
    not_attempted:     "Not attempted",
    unknown:           "Unknown",
};
const VERIFICATION_TONE = {
    verified:         "bg-emerald-900/40 text-emerald-200 border-emerald-700/50",
    version_mismatch: "bg-amber-900/40 text-amber-200 border-amber-700/50",
    name_mismatch:    "bg-amber-900/40 text-amber-200 border-amber-700/50",
    failed_install:   "bg-rose-900/40 text-rose-200 border-rose-700/50",
    manual_required:  "bg-slate-800/60 text-slate-200 border-slate-600/50",
    vendor_skipped:   "bg-zinc-800/60 text-zinc-200 border-zinc-600/50",
    not_attempted:    "bg-zinc-900/40 text-zinc-400 border-zinc-700/50",
    unknown:          "bg-zinc-900/40 text-zinc-400 border-zinc-700/50",
};

function VerificationSummary({ applyId }) {
    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(false);
    const [running, setRunning] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await http.get(`/migrations/apply/${applyId}/verification`);
            setReport(r.data);
        } catch (e) {
            if (e?.response?.status !== 404) toast.error("Verification fetch failed: " + errMsg(e));
        } finally {
            setLoading(false);
        }
    }, [applyId]);

    useEffect(() => { load(); }, [load]);

    const rerun = async () => {
        setRunning(true);
        try {
            await http.post(`/migrations/apply/${applyId}/verify`);
            await load();
            toast.success("Verification refreshed");
        } catch (e) {
            toast.error("Verification failed: " + errMsg(e));
        } finally {
            setRunning(false);
        }
    };

    const downloadCsv = async (path, fallbackName) => {
        try {
            const r = await http.get(`/migrations/apply/${applyId}/${path}`, { responseType: "blob" });
            const cd = r.headers?.["content-disposition"] || "";
            const m = cd.match(/filename="([^"]+)"/);
            const filename = m ? m[1] : fallbackName;
            const url = URL.createObjectURL(r.data);
            const a = document.createElement("a");
            a.href = url; a.download = filename;
            document.body.appendChild(a); a.click(); a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 1000);
        } catch (e) {
            toast.error("Download failed: " + errMsg(e));
        }
    };

    if (loading && !report) {
        return <div className="text-xs text-zinc-500 px-1 py-2"><Loader2 className="w-3 h-3 inline animate-spin mr-1" />Loading verification…</div>;
    }
    if (!report) return null;
    const counts = report.counts || {};
    const total = report.total || 0;
    return (
        <div className="rounded-md border border-zinc-800 bg-zinc-950/30 p-3 space-y-2" data-testid={`verification-summary-${applyId}`}>
            <div className="flex items-center justify-between">
                <div className="text-sm text-zinc-200 font-medium flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                    Post-Apply Verification
                    <span className="text-xs text-zinc-500">· {total} apps</span>
                    {!report.destination_scan_present && (
                        <span className="text-[10px] text-amber-300 ml-1">(awaiting destination scan)</span>
                    )}
                </div>
                <div className="flex items-center gap-1.5">
                    <Button size="sm" variant="outline" onClick={rerun} disabled={running} data-testid={`verification-refresh-${applyId}`}>
                        {running ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => downloadCsv("full-report.csv", "apply-report.csv")} data-testid={`verification-export-full-${applyId}`}>
                        Full report
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => downloadCsv("manual-checklist.csv", "manual-checklist.csv")} data-testid={`verification-export-manual-${applyId}`}>
                        Manual checklist
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => downloadCsv("vendor-checklist.csv", "vendor-checklist.csv")} data-testid={`verification-export-vendor-${applyId}`}>
                        Vendor checklist
                    </Button>
                </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-1.5" data-testid={`verification-counts-${applyId}`}>
                {Object.keys(VERIFICATION_LABEL).map(k => (
                    <div key={k}
                        className={`rounded-md border p-1.5 text-center ${VERIFICATION_TONE[k]}`}
                        data-testid={`verification-count-${k}`}>
                        <div className="text-[9px] uppercase tracking-wide opacity-80">{VERIFICATION_LABEL[k]}</div>
                        <div className="text-base font-semibold">{counts[k] || 0}</div>
                    </div>
                ))}
            </div>
        </div>
    );
}

export default function Migrations() {
    const [agents, setAgents]         = useState([]);
    const [captures, setCaptures]     = useState([]);
    const [loading, setLoading]       = useState(true);
    const [showStart, setShowStart]   = useState(false);
    const [reviewing, setReviewing]   = useState(null);  // capture being reviewed for apply

    // Deep-link: when arriving from the Agents page via "Migrate This PC",
    // ?source=<agent_id> opens the New Capture dialog with that agent
    // pre-selected. Cleared from the URL on first read so a manual page
    // refresh doesn't keep re-opening the dialog.
    const [searchParams, setSearchParams] = useSearchParams();
    const [presetSourceId, setPresetSourceId] = useState("");
    useEffect(() => {
        const src = searchParams.get("source");
        if (src) {
            setPresetSourceId(src);
            setShowStart(true);
            searchParams.delete("source");
            setSearchParams(searchParams, { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const refresh = useCallback(async () => {
        try {
            const [a, c] = await Promise.all([
                http.get("/agents"),
                http.get("/migrations"),
            ]);
            setAgents(a.data || []);
            setCaptures(c.data || []);
        } catch (e) {
            toast.error("Failed to load migrations: " + errMsg(e));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        refresh();
        // Light-weight polling (5s) for capture progress. The backend also
        // broadcasts ws events; this poll is the safety net for missed WS.
        // Pause polling while the review dialog is open — the user is
        // inspecting a snapshot and re-fetching the list every 5s would
        // introduce flicker without value.
        if (reviewing) return;
        const id = setInterval(refresh, 5000);
        return () => clearInterval(id);
    }, [refresh, reviewing]);

    const onDelete = async (jobId) => {
        if (!confirm("Delete this migration capture and all its artifacts?")) return;
        try {
            await http.delete(`/migrations/${jobId}`);
            toast.success("Capture deleted");
            await refresh();
        } catch (e) {
            toast.error("Delete failed: " + errMsg(e));
        }
    };

    return (
        <div className="mx-auto max-w-7xl space-y-6 p-6" data-testid="migrations-page">
            <PageHero
                eyebrow="PC Migration"
                title="Computer Migrations"
                description="Capture installed apps, registry, and system settings from a Windows host, then replay them on a destination PC."
                icon={Camera}
                tint="#5b5ef0"
                actions={
                    <>
                        <Button variant="outline" size="sm" onClick={refresh} data-testid="migrations-refresh-btn">
                            <RefreshCw className="w-4 h-4 mr-2" />Refresh
                        </Button>
                        <Button size="sm" onClick={() => setShowStart(true)} data-testid="migrations-new-btn">
                            <Camera className="w-4 h-4 mr-2" />New Capture
                        </Button>
                    </>
                }
            />

            <UsageInsightsWidget />

            <RecentMigrationsPulse items={adaptCaptures(captures)} testId="migrations-recent-pulse" />

            {loading ? (
                <div className="flex items-center justify-center py-20 text-zinc-400">
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />Loading migrations…
                </div>
            ) : captures.length === 0 ? (
                <EmptyState onStart={() => setShowStart(true)} />
            ) : (
                <div className="grid gap-4">
                    {captures.map(c => (
                        <CaptureCard
                            key={c.id}
                            capture={c}
                            onDelete={() => onDelete(c.id)}
                            onReview={() => setReviewing(c)}
                        />
                    ))}
                </div>
            )}

            <NewCaptureDialog
                open={showStart}
                onClose={() => { setShowStart(false); setPresetSourceId(""); }}
                agents={agents}
                presetSourceId={presetSourceId}
                onCreated={refresh}
            />

            {reviewing && (
                <ReviewAndApplyDialog
                    capture={reviewing}
                    agents={agents}
                    onClose={() => setReviewing(null)}
                    onApplied={refresh}
                />
            )}
        </div>
    );
}

// ----------------------- Usage insights widget ----------------------------
//
// Captured/applied bytes per calendar month for the active tenant.
// Backed by GET /api/migrations/insights/usage. Doubles as a soft
// upsell hook: "you migrated X TB this month — Pro tier covers
// unlimited, you're on Starter."
function UsageInsightsWidget() {
    const [data, setData] = useState(null);
    const [err, setErr] = useState(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data: d } = await http.get("/migrations/insights/usage", { params: { months: 6 } });
                if (!cancelled) setData(d);
            } catch (e) {
                if (!cancelled) setErr(errMsg(e));
            }
        })();
        return () => { cancelled = true; };
    }, []);

    if (err) {
        return null; // Silent on error — widget is non-critical.
    }
    if (!data) {
        return (
            <Card className="p-4 bg-zinc-900/40 border-zinc-800" data-testid="migrations-insights-loading">
                <div className="flex items-center text-xs text-zinc-500">
                    <Loader2 className="w-3 h-3 mr-2 animate-spin" />Loading usage…
                </div>
            </Card>
        );
    }

    const rows = data.rows || [];
    const totals = data.totals || {};
    const peak = Math.max(1, ...rows.map(r => r.captured_bytes));
    const plan = data.plan || "free";
    const capBytes = data.monthly_cap_bytes;
    const usagePct = data.usage_pct;
    const overLimit = usagePct != null && usagePct >= 100;
    const nearLimit = usagePct != null && usagePct >= 80;
    const showUpgrade = capBytes != null && plan !== "enterprise" && (nearLimit || overLimit);

    // Pick a non-aggressive accent color for the progress bar so the
    // widget stays "informational" until usage actually approaches the
    // cap. Red only when over.
    const barColor = overLimit
        ? "from-rose-500 to-rose-400"
        : nearLimit
          ? "from-amber-500 to-amber-400"
          : "from-emerald-500 to-emerald-400";

    return (
        <Card className="p-4 bg-zinc-900/40 border-zinc-800" data-testid="migrations-insights-widget">
            <div className="flex items-start justify-between mb-4">
                <div>
                    <div className="text-xs uppercase tracking-widest text-zinc-500">
                        Migration usage · last {data.months_window} months
                    </div>
                    <div className="mt-1 text-2xl font-semibold text-zinc-100" data-testid="migrations-insights-total-bytes">
                        {formatBytes(totals.captured_bytes || 0)}
                        <span className="ml-2 text-xs text-zinc-400 font-normal">captured</span>
                    </div>
                </div>
                <div className="grid grid-cols-3 gap-x-6 text-right">
                    <div>
                        <div className="text-[10px] uppercase tracking-widest text-zinc-500">Artifacts</div>
                        <div className="text-sm text-zinc-200" data-testid="migrations-insights-artifacts">
                            {totals.captured_artifacts || 0}
                        </div>
                    </div>
                    <div>
                        <div className="text-[10px] uppercase tracking-widest text-zinc-500">Apply jobs</div>
                        <div className="text-sm text-zinc-200" data-testid="migrations-insights-apply-jobs">
                            {totals.apply_jobs || 0}
                        </div>
                    </div>
                    <div>
                        <div className="text-[10px] uppercase tracking-widest text-zinc-500">Apps installed</div>
                        <div className="text-sm text-zinc-200" data-testid="migrations-insights-apps">
                            {totals.apps_installed || 0}
                        </div>
                    </div>
                </div>
            </div>

            {/* Per-plan monthly cap progress bar — soft upsell when ≥80%. */}
            <div className="mb-4" data-testid="migrations-insights-plan-row">
                <div className="flex items-center justify-between text-[11px] mb-1.5">
                    <div className="text-zinc-400">
                        <span className="uppercase tracking-widest text-zinc-500 mr-2">Plan</span>
                        <span className="text-zinc-200" data-testid="migrations-insights-plan-name">
                            {plan === "free" ? "Free" : plan === "basic" ? "Basic" : plan === "pro" ? "Pro" : "Enterprise"}
                        </span>
                        <span className="ml-3 text-zinc-500">
                            this month: <span className="text-zinc-300">{formatBytes(data.current_month_bytes || 0)}</span>
                            {capBytes != null && (
                                <span className="ml-1">/ {formatBytes(capBytes)}</span>
                            )}
                            {capBytes == null && (
                                <span className="ml-1 text-emerald-400">unlimited</span>
                            )}
                        </span>
                    </div>
                    {usagePct != null && (
                        <span
                            className={overLimit ? "text-rose-400" : nearLimit ? "text-amber-400" : "text-zinc-500"}
                            data-testid="migrations-insights-usage-pct"
                        >
                            {usagePct.toFixed(1)}%
                        </span>
                    )}
                </div>
                {capBytes != null && (
                    <div className="relative h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                        <div
                            className={`absolute inset-y-0 left-0 bg-gradient-to-r ${barColor} transition-all`}
                            style={{ width: `${Math.min(100, usagePct || 0)}%` }}
                            data-testid="migrations-insights-progress-fill"
                        />
                    </div>
                )}
                {showUpgrade && (
                    <div className="mt-2 flex items-center justify-between rounded-md border border-amber-500/30 bg-amber-500/5 px-3 py-2">
                        <div className="text-[11px] text-amber-200/90">
                            {overLimit
                                ? "You've hit this month's migration cap — upgrade to keep capturing without interruption."
                                : `You're approaching this month's cap (${usagePct.toFixed(0)}% used). Upgrade to avoid hitting the ceiling.`}
                        </div>
                        <a
                            href="/superadmin/billing"
                            className="ml-3 shrink-0 inline-flex items-center gap-1 rounded-sm bg-amber-500 px-2.5 py-1 text-[11px] font-medium text-zinc-900 hover:bg-amber-400"
                            data-testid="migrations-insights-upgrade-link"
                        >
                            Upgrade
                            <ArrowRight className="w-3 h-3" />
                        </a>
                    </div>
                )}
            </div>

            {rows.length === 0 ? (
                <div className="text-xs text-zinc-500" data-testid="migrations-insights-empty">
                    No captures yet — your first migration will show up here.
                </div>
            ) : (
                <div className="grid grid-cols-6 gap-2 items-end h-24" data-testid="migrations-insights-bars">
                    {rows.map((r) => {
                        const pct = Math.round((r.captured_bytes / peak) * 100);
                        return (
                            <div key={r.month} className="flex flex-col items-center gap-1.5">
                                <div className="w-full flex items-end h-16">
                                    <div
                                        className="w-full bg-gradient-to-t from-[#5b5ef0] to-[#8b8df0] rounded-t-sm transition-all"
                                        style={{ height: `${Math.max(2, pct)}%` }}
                                        title={`${r.month}: ${formatBytes(r.captured_bytes)} captured · ${r.apps_installed} apps installed`}
                                        data-testid={`migrations-insights-bar-${r.month}`}
                                    />
                                </div>
                                <div className="text-[10px] text-zinc-500 font-mono">
                                    {r.month.slice(5)}
                                </div>
                                <div className="text-[10px] text-zinc-300 font-mono">
                                    {formatBytes(r.captured_bytes).replace(" ", "")}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </Card>
    );
}

// ----------------------- Empty state ---------------------------------------

function EmptyState({ onStart }) {
    return (
        <Card className="rounded-xl border-dashed border-border bg-surface p-12 text-center" data-testid="migrations-empty-state">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-xl border border-[#5b5ef0]/30 bg-[#5b5ef0]/10 text-[#5b5ef0]">
                <Monitor className="h-6 w-6" />
            </div>
            <h3 className="font-display text-lg font-semibold">No migrations captured yet</h3>
            <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">
                Capture a Windows PC&apos;s apps, registry, and settings. The agent runs the inventory
                in the background and uploads the artifacts here for review.
            </p>
            <Button className="mt-6" onClick={onStart} data-testid="migrations-empty-start-btn">
                <Camera className="w-4 h-4 mr-2" />Start Capture
            </Button>
        </Card>
    );
}

// ----------------------- Capture card --------------------------------------

function CaptureCard({ capture, onDelete, onReview }) {
    const phases = capture.phases || {};
    const phaseEntries = Object.entries(phases);
    const completedCount = phaseEntries.filter(([, v]) => v === "completed").length;
    const total = phaseEntries.length || 1;
    const progress = Math.round((completedCount / total) * 100);

    // A capture stuck in "capturing" status with every phase still "queued"
    // for more than ~1 minute almost always means the source agent never
    // pulled the commands — agent /commands poll interval is 5s, so a
    // healthy agent flips at least one phase to "in-progress" within
    // that window. Surface a clear hint instead of leaving the operator
    // staring at an indefinite spinner. Common causes:
    //   - The agent is a simulated test row (no real C# process)
    //   - The agent is offline / draining
    //   - The installed agent is older than v1.0.7 (no MigrationCommandHandler)
    const allQueued =
        capture.status === "capturing" &&
        phaseEntries.length > 0 &&
        phaseEntries.every(([, v]) => v === "queued");
    const ageMin = capture.created_at
        ? (Date.now() - new Date(capture.created_at).getTime()) / 60000
        : 0;
    const stuckHint = allQueued && ageMin > 1;

    return (
        <Card
            className="rounded-xl border-border bg-surface p-5 shadow-sm transition-all hover:-translate-y-0.5 hover:border-foreground/20 hover:shadow-md"
            data-testid={`capture-card-${capture.id}`}
        >
            <div className="flex items-start justify-between mb-3">
                <div>
                    <div className="flex items-center gap-2">
                        <span className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-[#5b5ef0]/10 text-[#5b5ef0]">
                            <Monitor className="h-3.5 w-3.5" />
                        </span>
                        <span className="font-display font-semibold text-foreground" data-testid={`capture-source-${capture.id}`}>
                            {capture.source_agent_name || capture.source_agent_id.slice(0, 8)}
                        </span>
                        {(() => {
                            const meta = osBadge(normalizeOs(capture.source_os));
                            return capture.source_os ? (
                                <span
                                    className="inline-flex items-center gap-1 font-mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded border"
                                    style={{ color: meta.color, borderColor: `${meta.color}55`, backgroundColor: `${meta.color}10` }}
                                    data-testid={`capture-os-badge-${capture.id}`}
                                >
                                    <span
                                        className="inline-block h-1.5 w-1.5 rounded-full"
                                        style={{ backgroundColor: meta.dot }}
                                        aria-hidden
                                    />
                                    {meta.short}
                                </span>
                            ) : null;
                        })()}
                        <StatusPill status={capture.status} />
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                        Created {new Date(capture.created_at).toLocaleString()}
                        {capture.completed_at && <> · Completed {new Date(capture.completed_at).toLocaleString()}</>}
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {capture.status === "completed" && (
                        <Button size="sm" onClick={onReview} data-testid={`capture-review-btn-${capture.id}`}>
                            Review &amp; Apply<ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={onDelete} data-testid={`capture-delete-btn-${capture.id}`}>
                        <Trash2 className="w-4 h-4" />
                    </Button>
                </div>
            </div>

            <div className="space-y-2 mt-4">
                {stuckHint && (
                    <div
                        className="flex items-start gap-2 p-3 rounded-md bg-amber-950/30 border border-amber-800/50"
                        data-testid={`capture-stuck-hint-${capture.id}`}
                    >
                        <AlertCircle className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
                        <div className="text-xs text-amber-200 leading-relaxed">
                            <div className="font-medium">No phase has started after {Math.round(ageMin)} minute(s).</div>
                            <div className="mt-1 text-amber-200/80">
                                The source agent hasn't pulled the migration commands. Common causes:
                                <ul className="list-disc list-inside mt-1 space-y-0.5">
                                    <li>The agent is a simulated test agent (no real C# process running)</li>
                                    <li>The installed agent version is older than <span className="font-mono">v1.0.7</span> (which introduced the migration command handlers) — uninstall and reinstall from <em>Download Agent</em></li>
                                    <li>The agent is offline or in <em>drained</em> mode</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                )}
                {phaseEntries.map(([key, status]) => {
                    const meta = PHASE_META[key] || { label: key, icon: FileText };
                    const Icon = meta.icon;
                    const detail = capture.phase_details?.[key];

                    // Visual feedback per phase status. The in-progress phase
                    // is the only one that should call attention to itself —
                    // operators need to know the agent is *actually doing
                    // something* right now, not just that some-day-this-may-
                    // finish. So we tint the row blue and animate the icon.
                    // Completed = subtle green tint with a check; failed =
                    // rose tint with the original icon (so context is clear).
                    const rowCls = {
                        "in-progress": "bg-blue-950/40 border-l-2 border-blue-500",
                        completed:     "bg-emerald-950/20 border-l-2 border-emerald-500/60",
                        failed:        "bg-rose-950/30 border-l-2 border-rose-500",
                        queued:        "bg-zinc-950/50 border-l-2 border-transparent",
                    }[status] || "bg-zinc-950/50";
                    const iconCls = {
                        "in-progress": "text-blue-400 animate-pulse",
                        completed:     "text-emerald-400",
                        failed:        "text-rose-400",
                        queued:        "text-zinc-500",
                    }[status] || "text-zinc-500";

                    return (
                        <div key={key}
                            className={`flex items-center justify-between py-2 px-3 rounded-md transition-colors ${rowCls}`}
                            data-testid={`capture-phase-row-${capture.id}-${key}`}>
                            <div className="flex items-center gap-3 min-w-0">
                                {status === "in-progress"
                                    ? <Loader2 className={`w-4 h-4 shrink-0 ${iconCls} animate-spin`} />
                                    : status === "completed"
                                        ? <CheckCircle2 className={`w-4 h-4 shrink-0 ${iconCls}`} />
                                        : <Icon className={`w-4 h-4 shrink-0 ${iconCls}`} />}
                                <span className="text-sm text-foreground shrink-0">{meta.label}</span>
                                {detail && <span className="text-xs text-muted-foreground truncate">— {detail}</span>}
                            </div>
                            <PhaseBadge status={status} />
                        </div>
                    );
                })}
            </div>

            <div className="mt-4">
                <div className="flex items-center justify-between text-xs text-muted-foreground mb-1.5">
                    <span data-testid={`capture-progress-label-${capture.id}`}>
                        <span className="font-semibold text-foreground">{progress}%</span>
                        <span className="ml-2">{completedCount} / {total} phases</span>
                    </span>
                    <span>{capture.artifacts?.length || 0} artifact{(capture.artifacts?.length || 0) === 1 ? "" : "s"}</span>
                </div>
                {/* Taller, animated progress bar. While the capture is
                    actively running we add a barber-pole stripe via a CSS
                    keyframe so users can see progress is *live*, not
                    frozen. Failed / completed states show solid colour. */}
                <div className="h-2 rounded-full bg-border overflow-hidden">
                    <div
                        className={`h-full transition-all duration-500
                            ${capture.status === "failed"
                                ? "bg-rose-500"
                                : capture.status === "completed"
                                    ? "bg-emerald-500"
                                    : "bg-blue-500 animate-progress-stripes"}`}
                        style={{
                            width: `${progress}%`,
                            backgroundImage: capture.status === "capturing"
                                ? "linear-gradient(45deg, rgba(255,255,255,.15) 25%, transparent 25%, transparent 50%, rgba(255,255,255,.15) 50%, rgba(255,255,255,.15) 75%, transparent 75%)"
                                : undefined,
                            backgroundSize: capture.status === "capturing" ? "1rem 1rem" : undefined,
                        }}
                        data-testid={`capture-progress-${capture.id}`}
                    />
                </div>
            </div>

            {/* Per-app install drawer for any apply triggered from this
                capture. Only renders if at least one apply exists.
                Collapsed by default to keep the card compact. */}
            <AppStatusDrawer captureId={capture.id} captureStatus={capture.status} />

            {/* Phase 3: App Inventory drawer — per-app overrides + create
                custom mapping from row. Renders for completed captures
                (and during capturing, so operators can preview as soon
                as apps.json lands). */}
            <AppInventoryDrawer captureId={capture.id} captureStatus={capture.status} />
        </Card>
    );
}

// ----------------------- New capture dialog --------------------------------
//
// Multi-OS aware: when an operator picks a source agent we read its `os`
// field, normalise it (windows | macos | linux), and morph the dialog —
// phase checkboxes ("Apps / Registry / Settings" on Windows; "Packages /
// Config" on Linux; "Applications / Preferences" on macOS) and the
// user-profile folder picker (C:\Users\<u>\… vs /Users/<u>/… vs
// /home/<u>/…). Source dropdown shows a coloured OS badge per agent so
// operators see at-a-glance what platform mix they're running.

function NewCaptureDialog({ open, onClose, agents, onCreated, presetSourceId }) {
    const [agentId, setAgentId] = useState("");
    const [agentSearch, setAgentSearch] = useState("");
    // OS-specific phase toggles. Defaults per OS preset come from the
    // `CAPTURE_PHASES_BY_OS` catalogue. Stored as an object keyed by
    // backend phase-flag name (`include_apps`, `include_registry`, …).
    const [phases, setPhases] = useState({});
    const [includeProfile, setIncludeProfile] = useState(true);
    // Phase 4: per-source-agent carry-overrides toggle. Defaults OFF so
    // a fresh capture for a new host never inherits another machine's
    // triage by accident. Operators flip this on for monthly re-captures
    // of the same source PC.
    const [carryOverrides, setCarryOverrides] = useState(false);
    const [profileFolders, setProfileFolders] = useState(new Set());
    const [extraPaths, setExtraPaths] = useState([]); // [{path, label, type}]
    const [submitting, setSubmitting] = useState(false);
    const [browseOpen, setBrowseOpen] = useState(false);

    // Filtered agent list — Select dropdown shows only the agents that
    // match the search query (free-text over name/hostname/id). Keeps
    // the currently-selected agent visible even when it would otherwise
    // be filtered out, so the SelectValue label still resolves.
    const visibleAgents = useMemo(() => {
        const matched = filterByName(agents, agentSearch, ["name", "hostname", "id"]);
        if (agentId && !matched.find((a) => a.id === agentId)) {
            const picked = agents.find((a) => a.id === agentId);
            if (picked) return [picked, ...matched];
        }
        return matched;
    }, [agents, agentSearch, agentId]);

    // Compute the active OS from the picked agent. We accept the
    // agent's self-reported `os` field — if it's missing, fall back to
    // null and surface a "OS not detected" warning so the operator
    // knows we couldn't morph the dialog.
    const selectedAgent = useMemo(
        () => agents.find(a => a.id === agentId) || null,
        [agents, agentId],
    );
    const activeOs = useMemo(() => normalizeOs(selectedAgent?.os), [selectedAgent]);
    const phaseCatalog = activeOs ? CAPTURE_PHASES_BY_OS[activeOs] : [];
    const folderCatalog = activeOs ? PROFILE_FOLDERS_BY_OS[activeOs] : [];

    // Reset → defaults whenever the active OS changes (or the dialog
    // re-opens). Without this, switching from a Windows agent to a
    // Linux one would carry stale Windows folder selections forward.
    useEffect(() => {
        if (!open) {
            setAgentId(""); setPhases({}); setIncludeProfile(true); setProfileFolders(new Set());
        } else if (presetSourceId) {
            setAgentId(presetSourceId);
        }
    }, [open, presetSourceId]);

    useEffect(() => {
        if (!activeOs) { setPhases({}); setProfileFolders(new Set()); return; }
        const def = {};
        for (const p of CAPTURE_PHASES_BY_OS[activeOs]) def[p.key] = p.defaultOn;
        setPhases(def);
        setProfileFolders(new Set(
            PROFILE_FOLDERS_BY_OS[activeOs].filter(f => f.defaultOn).map(f => f.rel),
        ));
    }, [activeOs]);

    const togglePhase = (k) => setPhases(prev => ({ ...prev, [k]: !prev[k] }));
    const toggleFolder = (rel) => setProfileFolders(prev => {
        const next = new Set(prev);
        if (next.has(rel)) next.delete(rel); else next.add(rel);
        return next;
    });

    const submit = async () => {
        if (!agentId) { toast.error("Pick a source agent"); return; }
        if (!activeOs) { toast.error("Source agent OS could not be detected — re-deploy the agent or pick another host"); return; }
        if (includeProfile && profileFolders.size === 0) {
            toast.error("Pick at least one user-profile folder, or untick 'User profile'");
            return;
        }
        setSubmitting(true);
        try {
            const body = {
                agent_id: agentId,
                source_os: activeOs, // backend stamps this on the capture row for downstream OS-aware logic
                include_apps:     !!phases.include_apps,
                include_registry: !!phases.include_registry,
                include_settings: !!phases.include_settings,
                include_profile_estimate: includeProfile,
                include_profile_capture:  includeProfile,
                selected_profile_folders: includeProfile ? Array.from(profileFolders) : [],
                extra_capture_paths:      extraPaths,
                carry_overrides_from_agent: carryOverrides,
            };
            const { data } = await http.post("/migrations/capture", body);
            toast.success(`Capture started — ${Object.keys(data.phases).length} phase(s) queued`);
            onClose();
            onCreated();
        } catch (e) {
            toast.error("Failed to start capture: " + errMsg(e));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent data-testid="new-capture-dialog" className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>New PC Migration</DialogTitle>
                    <DialogDescription>
                        Snapshot a host's apps, settings, and user profile in one shot.
                        <span className="text-zinc-400"> Windows · macOS · Linux supported</span> — the dialog adapts to the selected agent's platform.
                    </DialogDescription>
                </DialogHeader>

                <StepIndicator
                    steps={["Source", "Phases", "Capture", "Review & Apply"]}
                    currentIndex={agentId ? 1 : 0}
                    testIdPrefix="capture-step"
                />

                <div className="space-y-4">
                    <div>
                        <label className="text-sm font-medium text-zinc-300">Source Agent</label>
                        <div className="mt-1.5 mb-2">
                            <SearchInput
                                value={agentSearch}
                                onChange={setAgentSearch}
                                placeholder={`Search devices by name or hostname… (${agents.length} online)`}
                                testid="capture-source-search"
                            />
                        </div>
                        <Select value={agentId} onValueChange={setAgentId}>
                            <SelectTrigger className="mt-1.5" data-testid="capture-source-select">
                                <SelectValue placeholder={agents.length === 0 ? "No agents online" : visibleAgents.length === 0 ? `No matches for "${agentSearch}"` : "Select a host"} />
                            </SelectTrigger>
                            <SelectContent>
                                {visibleAgents.map(a => {
                                    const ag = osBadge(normalizeOs(a.os));
                                    return (
                                        <SelectItem key={a.id} value={a.id} data-testid={`capture-source-option-${a.id}`}>
                                            <span className="inline-flex items-center gap-2">
                                                <span
                                                    className="inline-block h-2 w-2 rounded-full"
                                                    style={{ backgroundColor: ag.dot }}
                                                    aria-hidden
                                                />
                                                <span className="font-mono text-[10px] uppercase tracking-wider text-zinc-400">
                                                    {ag.short}
                                                </span>
                                                <span>{a.name || a.hostname || a.id.slice(0, 8)}</span>
                                                <span className="text-zinc-500">— {a.status}</span>
                                            </span>
                                        </SelectItem>
                                    );
                                })}
                            </SelectContent>
                        </Select>
                        {selectedAgent && !activeOs && (
                            <p className="text-xs text-amber-400 mt-2" data-testid="capture-os-undetected">
                                Source agent OS could not be detected ({selectedAgent.os || "—"}). Re-deploy the agent so it self-reports its platform, or pick another host.
                            </p>
                        )}
                        {selectedAgent && activeOs && (
                            <p className="text-[10px] text-zinc-500 mt-1.5">
                                Detected platform:{" "}
                                <span
                                    className="font-mono uppercase tracking-wider"
                                    style={{ color: osBadge(activeOs).color }}
                                >
                                    {osBadge(activeOs).label}
                                </span>{" "}
                                — phases and profile folders below are tailored to it.
                            </p>
                        )}
                        {selectedAgent && selectedAgent.status === "online" && (
                            <div className="mt-2 flex flex-wrap items-center gap-1.5">
                                <button
                                    type="button"
                                    onClick={() => setBrowseOpen(true)}
                                    data-testid="capture-browse-agent-btn"
                                    className="inline-flex items-center gap-1.5 rounded border border-zinc-700 bg-zinc-900/60 px-2.5 py-1 text-[11px] font-medium text-zinc-300 hover:border-blue-500/60 hover:text-blue-300"
                                >
                                    <FolderTree size={12} />
                                    {extraPaths.length === 0
                                        ? "Add storage paths from this agent"
                                        : `Edit ${extraPaths.length} extra path(s)`}
                                </button>
                                {extraPaths.slice(0, 2).map((p) => (
                                    <span
                                        key={p.path}
                                        data-testid={`capture-extra-summary-${p.path}`}
                                        className="rounded border border-emerald-500/30 bg-emerald-500/10 px-1.5 py-0.5 font-mono text-[10px] text-emerald-200"
                                        title={p.path}
                                    >
                                        {p.path.split(/[\\/]/).pop() || p.path}
                                    </span>
                                ))}
                                {extraPaths.length > 2 && (
                                    <span className="text-[10px] text-zinc-500">+{extraPaths.length - 2} more</span>
                                )}
                            </div>
                        )}
                    </div>

                    {activeOs && (
                        <div>
                            <div className="text-sm font-medium text-zinc-300 mb-2">What to capture</div>
                            <div className="space-y-2">
                                {phaseCatalog.map((p) => (
                                    <label
                                        key={p.key}
                                        className="flex items-start gap-3 p-3 rounded-md bg-zinc-950/50 border border-zinc-800 cursor-pointer hover:border-zinc-700"
                                        data-testid={`capture-toggle-${p.key}`}
                                    >
                                        <Checkbox
                                            checked={!!phases[p.key]}
                                            onCheckedChange={() => togglePhase(p.key)}
                                            className="mt-0.5"
                                        />
                                        <div className="flex-1">
                                            <div className="text-sm font-medium text-zinc-200">{p.label}</div>
                                            <div className="text-xs text-zinc-500">{p.desc}</div>
                                        </div>
                                    </label>
                                ))}

                                <label
                                    className="flex items-start gap-3 p-3 rounded-md bg-zinc-950/50 border border-zinc-800 cursor-pointer hover:border-zinc-700"
                                    data-testid="capture-toggle-include_profile"
                                >
                                    <Checkbox checked={includeProfile} onCheckedChange={setIncludeProfile} className="mt-0.5" />
                                    <div className="flex-1">
                                        <div className="text-sm font-medium text-zinc-200">
                                            User profile
                                            <span className="ml-2 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                                                multi-part — large folders ship as chunked archives
                                            </span>
                                        </div>
                                        <div className="text-xs text-zinc-500">
                                            Capture the operator's selection of{" "}
                                            <code className="text-zinc-400">
                                                {activeOs === "windows" ? "C:\\Users\\<active-user>\\" : `/${activeOs === "macos" ? "Users" : "home"}/<active-user>/`}
                                            </code>{" "}
                                            top-level folders. Folders &gt;5 GB are split into multi-part archives — never skipped for size.
                                        </div>
                                    </div>
                                </label>

                                {includeProfile && (
                                    <div
                                        className="ml-6 p-3 rounded-md bg-zinc-950/40 border border-zinc-800/60 space-y-1.5"
                                        data-testid="capture-profile-folder-picker"
                                    >
                                        <div className="flex items-center justify-between mb-1">
                                            <div className="text-xs uppercase tracking-wider text-zinc-500">
                                                {osBadge(activeOs).label} folders to migrate
                                            </div>
                                            <div className="flex items-center gap-1.5">
                                                <button
                                                    type="button"
                                                    onClick={() => setProfileFolders(new Set(folderCatalog.map(f => f.rel)))}
                                                    className="text-[11px] text-zinc-400 hover:text-zinc-200"
                                                    data-testid="capture-profile-select-all-btn"
                                                >Select all</button>
                                                <span className="text-zinc-600">·</span>
                                                <button
                                                    type="button"
                                                    onClick={() => setProfileFolders(new Set())}
                                                    className="text-[11px] text-zinc-400 hover:text-zinc-200"
                                                    data-testid="capture-profile-clear-btn"
                                                >Clear</button>
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                                            {folderCatalog.map(f => {
                                                const on = profileFolders.has(f.rel);
                                                return (
                                                    <label
                                                        key={f.rel}
                                                        className={`flex items-center gap-2 p-1.5 rounded border cursor-pointer text-xs ${
                                                            on ? "bg-emerald-950/30 border-emerald-800/60"
                                                               : "bg-zinc-900/40 border-zinc-800/60 hover:border-zinc-700"}`}
                                                        data-testid={`capture-profile-folder-${f.rel.replace(/[\\/.]/g, '-')}`}
                                                    >
                                                        <Checkbox checked={on} onCheckedChange={() => toggleFolder(f.rel)} />
                                                        <span className="text-zinc-300 truncate">{f.label}</span>
                                                    </label>
                                                );
                                            })}
                                        </div>
                                        <div className="text-[10px] text-zinc-500 pt-1">
                                            Profile size estimate runs alongside the capture so the dashboard shows per-folder bytes.
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* Phase 4: carry overrides forward (per-source-agent
                    scope). Tucked under the phase grid as a workflow
                    nicety, not a phase — only matters when the same
                    source agent has been captured before. */}
                <div className="mt-3 flex items-start gap-2 rounded-md border border-zinc-800 bg-zinc-950/40 p-3">
                    <input
                        id="capture-carry-overrides"
                        type="checkbox"
                        checked={carryOverrides}
                        onChange={(e) => setCarryOverrides(e.target.checked)}
                        className="mt-0.5 accent-[#00C2FF]"
                        data-testid="capture-carry-overrides"
                    />
                    <label htmlFor="capture-carry-overrides" className="text-xs text-zinc-300">
                        <span className="text-sm text-zinc-200 font-medium">Carry app-resolver overrides forward</span>
                        <div className="text-[11px] text-zinc-500">
                            When the same source agent has been captured before, copy every
                            per-app override (category, custom mapping, skip reason) from the
                            most recent capture into this one. Scope: this source agent only.
                        </div>
                    </label>
                </div>

                <DialogFooter>
                    <Button variant="ghost" onClick={onClose} data-testid="capture-cancel-btn">Cancel</Button>
                    <Button
                        onClick={submit}
                        disabled={submitting || !agentId || !activeOs}
                        data-testid="capture-start-btn"
                    >
                        {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Camera className="w-4 h-4 mr-2" />}
                        Start Capture
                    </Button>
                </DialogFooter>
            </DialogContent>

            {/* Nested FsBrowser dialog — operator can both inspect the
                source PC layout AND pick directories to add to the
                capture set (NAS shares, alternate drives, network
                mounts, etc.). The picked paths flow through to the
                agent as ``extra_paths`` on the capture-profile command. */}
            {browseOpen && selectedAgent && (
                <Dialog open={true} onOpenChange={(o) => !o && setBrowseOpen(false)}>
                    <DialogContent className="max-w-2xl" data-testid="capture-browse-dialog">
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2">
                                <FolderTree size={16} /> Add storage paths from {selectedAgent.name || selectedAgent.hostname}
                            </DialogTitle>
                            <DialogDescription>
                                Click <strong>Select</strong> on any directory to add it to the capture
                                set. NAS shares the agent has access to also enumerate via the NAS tab.
                            </DialogDescription>
                        </DialogHeader>
                        <CaptureBrowsePanel
                            agentId={selectedAgent.id}
                            extraPaths={extraPaths}
                            onChange={setExtraPaths}
                            onClose={() => setBrowseOpen(false)}
                        />
                    </DialogContent>
                </Dialog>
            )}
        </Dialog>
    );
}

// ---- Capture browse panel ---------------------------------------------------
// Hosts the FsBrowser with kind toggle (Local/NAS) + a side rail showing
// which paths the operator has selected. Clicking Select on a directory
// in the FsBrowser appends it; clicking the X on a chip removes it.
function CaptureBrowsePanel({ agentId, extraPaths, onChange, onClose }) {
    const [kind, setKind] = useState("local");

    const togglePath = (entry) => {
        const exists = extraPaths.find((p) => p.path === entry.path);
        if (exists) {
            onChange(extraPaths.filter((p) => p.path !== entry.path));
        } else {
            onChange([...extraPaths, { path: entry.path, label: entry.path, type: entry.type }]);
        }
    };
    const removePath = (path) => onChange(extraPaths.filter((p) => p.path !== path));

    return (
        <>
            <div className="mb-3 inline-flex rounded border border-zinc-800 bg-zinc-900/40 p-0.5" role="tablist">
                {[
                    { v: "local", label: "Local drives" },
                    { v: "nas",   label: "NAS shares" },
                ].map((t) => (
                    <button
                        key={t.v}
                        onClick={() => setKind(t.v)}
                        data-testid={`capture-browse-kind-${t.v}`}
                        role="tab"
                        aria-selected={kind === t.v}
                        className={[
                            "rounded px-3 py-1 text-xs font-medium",
                            kind === t.v ? "bg-blue-500 text-white" : "text-zinc-400 hover:text-zinc-200",
                        ].join(" ")}
                    >
                        {t.label}
                    </button>
                ))}
            </div>

            <FsBrowser agentId={agentId} kind={kind} key={kind} onSelect={togglePath} />

            <div className="mt-4 rounded border border-zinc-800 bg-zinc-950/40 p-3" data-testid="capture-extra-paths-list">
                <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">
                    Selected paths ({extraPaths.length})
                </div>
                {extraPaths.length === 0 ? (
                    <div className="text-xs text-zinc-600">No paths selected. Browse and click Select on a directory.</div>
                ) : (
                    <div className="flex flex-wrap gap-1.5">
                        {extraPaths.map((p) => (
                            <span
                                key={p.path}
                                data-testid={`capture-extra-chip-${p.path}`}
                                className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-2 py-0.5 font-mono text-[11px] text-emerald-200"
                            >
                                {p.path}
                                <button
                                    onClick={() => removePath(p.path)}
                                    className="text-emerald-300 hover:text-white"
                                    aria-label={`Remove ${p.path}`}
                                    data-testid={`capture-extra-chip-remove-${p.path}`}
                                >
                                    <X size={10} />
                                </button>
                            </span>
                        ))}
                    </div>
                )}
            </div>

            <DialogFooter>
                <Button variant="ghost" onClick={onClose} data-testid="capture-browse-close-btn">
                    Done
                </Button>
            </DialogFooter>
        </>
    );
}

// ----------------------- Review & Apply dialog -----------------------------

/**
 * The key used to select an app for auto-install: its winget ID when
 * present, otherwise its MSI product code — but ONLY when the agent
 * actually captured a cached copy of that MSI during scan-apps
 * (`msi_cache_captured`). Without a captured cache there's no installer
 * media to replay on the destination, so the row stays disabled and
 * routes to the manual checklist instead (see apply_planner.py).
 * Returns null for apps that aren't auto-installable at all.
 */
function installKey(app) {
    if (app.winget_id) return app.winget_id;
    if (app.msi_product_code && app.msi_cache_captured) return app.msi_product_code;
    if (app.portable_cache_captured && app.app_key) return app.app_key;
    return null;
}

function ReviewAndApplyDialog({ capture, agents, onClose, onApplied }) {
    const [apps, setApps]         = useState(null);
    const [settings, setSettings] = useState(null);
    const [loading, setLoading]   = useState(true);
    const [selected, setSelected] = useState(new Set());
    const [destId, setDestId]     = useState("");
    const [includeReg, setIncludeReg]   = useState(true);
    const [includeSet, setIncludeSet]   = useState(true);
    // Whether to apply the previously-captured user profile (Phase 2 of
    // the user-profile feature). Default off because not every capture
    // has a profile attached — auto-toggled on below if the capture's
    // ``capture-profile`` phase is present.
    const [includeProfile, setIncludeProfile] = useState(false);
    const hasProfileCapture = !!capture.phases?.["capture-profile"];
    useEffect(() => { if (hasProfileCapture) setIncludeProfile(true); }, [hasProfileCapture]);
    const [applying, setApplying] = useState(false);

    // App-list polish: search box, "show only selected" toggle, and bulk
    // select/deselect over the visible (filtered) rows. Helps when the host
    // has 200+ installed apps — without these the operator scrolls forever.
    const [search, setSearch] = useState("");
    const [showOnlySelected, setShowOnlySelected] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const [a, s] = await Promise.all([
                    http.get(`/migrations/${capture.id}/apps`).catch(() => ({ data: [] })),
                    http.get(`/migrations/${capture.id}/settings`).catch(() => ({ data: null })),
                ]);
                if (cancelled) return;
                setApps(Array.isArray(a.data) ? a.data : []);
                setSettings(s.data);
                // pre-select every auto-installable app (winget, or MSI with
                // a captured cache)
                const initial = new Set();
                (Array.isArray(a.data) ? a.data : []).forEach(app => {
                    const key = installKey(app);
                    if (key) initial.add(key);
                });
                setSelected(initial);
            } catch (e) {
                toast.error("Failed to load capture details: " + errMsg(e));
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [capture.id]);

    // Show ALL other agents — operator can soft-override the same-OS
    // guardrail. Mismatched-OS rows get a warning badge in the dropdown
    // and a confirm dialog gates the actual Apply submission. Hard
    // filtering ruled out: an Engineer might intentionally re-target an
    // OS variant (e.g. Windows 10 → Windows 11 reported as "windows" on
    // both, but also legitimate cross-OS recovery scenarios). See
    // ADR-2026-04-30 in CHANGELOG.
    const captureOs = normalizeOs(capture.source_os);
    const otherAgents = useMemo(
        () => agents.filter(a => a.id !== capture.source_agent_id),
        [agents, capture.source_agent_id],
    );
    const destAgent = useMemo(
        () => otherAgents.find(a => a.id === destId) || null,
        [otherAgents, destId],
    );
    const destOs = destAgent ? normalizeOs(destAgent.os) : null;
    const osMismatch = !!(captureOs && destOs && captureOs !== destOs);

    // Confirm-dialog state — gates Apply when the operator picked a
    // mismatched-OS destination. Operator must explicitly acknowledge.
    const [confirmMismatch, setConfirmMismatch] = useState(false);

    const toggle = (key) => {
        setSelected(prev => {
            const n = new Set(prev);
            n.has(key) ? n.delete(key) : n.add(key);
            return n;
        });
    };

    // Filtered + sorted view of the app list. Memoized because the operator
    // types into the search box often and we don't want to re-walk a 500-row
    // list on every keystroke. Apps that aren't auto-installable are still
    // shown (so the user can see what's installed) but their checkbox is
    // disabled — see installKey().
    const visibleApps = useMemo(() => {
        const q = search.trim().toLowerCase();
        return (apps || []).filter(app => {
            const key = installKey(app);
            if (showOnlySelected && (!key || !selected.has(key))) return false;
            if (!q) return true;
            return (
                app.name?.toLowerCase().includes(q) ||
                app.publisher?.toLowerCase().includes(q) ||
                app.winget_id?.toLowerCase().includes(q) ||
                app.msi_product_code?.toLowerCase().includes(q)
            );
        });
    }, [apps, search, showOnlySelected, selected]);

    // Map of selectable install keys in the visible (post-filter) view —
    // used by Select-All / Deselect-All so bulk ops only affect what the
    // user can see right now (mirrors how Gmail handles bulk-select after
    // filter).
    const visibleMappableIds = useMemo(
        () => visibleApps.map(installKey).filter(Boolean),
        [visibleApps],
    );
    const allVisibleSelected =
        visibleMappableIds.length > 0 &&
        visibleMappableIds.every(id => selected.has(id));

    const toggleSelectAllVisible = () => {
        setSelected(prev => {
            const n = new Set(prev);
            if (allVisibleSelected) {
                visibleMappableIds.forEach(id => n.delete(id));
            } else {
                visibleMappableIds.forEach(id => n.add(id));
            }
            return n;
        });
    };

    const submit = async (skipConfirm = false) => {
        if (!destId) { toast.error("Pick a destination agent"); return; }
        if (osMismatch && !skipConfirm) {
            // Open confirm dialog instead of submitting directly. The
            // dialog calls submit(true) on confirm.
            setConfirmMismatch(true);
            return;
        }
        setApplying(true);
        try {
            // Split the unified selection back into winget IDs vs MSI
            // product codes vs portable app_keys — each lives in its own
            // request field (selected_winget_ids / selected_msi_products /
            // selected_portable_apps) since the agent needs to know which
            // download+install path to use.
            const msiCodes = new Set(
                (apps || [])
                    .filter(a => a.msi_product_code && a.msi_cache_captured && selected.has(a.msi_product_code))
                    .map(a => a.msi_product_code),
            );
            const portableKeys = new Set(
                (apps || [])
                    .filter(a => a.portable_cache_captured && a.app_key && selected.has(a.app_key))
                    .map(a => a.app_key),
            );
            const { data } = await http.post("/migrations/apply", {
                capture_job_id: capture.id,
                destination_agent_id: destId,
                selected_winget_ids: Array.from(selected).filter(k => !msiCodes.has(k) && !portableKeys.has(k)),
                selected_msi_products: Array.from(msiCodes),
                selected_portable_apps: Array.from(portableKeys),
                include_registry: includeReg,
                include_settings: includeSet,
                include_profile: includeProfile,
            });
            toast.success(`Apply queued — ${Object.keys(data.phases).length} phase(s)`);
            onClose();
            onApplied();
        } catch (e) {
            toast.error("Apply failed: " + errMsg(e));
        } finally {
            setApplying(false);
            setConfirmMismatch(false);
        }
    };

    return (
        <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="review-apply-dialog">
                <DialogHeader>
                    <DialogTitle>Review &amp; Apply Migration</DialogTitle>
                    <DialogDescription>
                        Captured on {new Date(capture.created_at).toLocaleString()} from {capture.source_agent_name}.
                    </DialogDescription>
                </DialogHeader>

                {/* Apply-side wizard steps. We're past Source/Phases/Capture
                    by the time this dialog opens (the capture is already
                    complete), so show steps 4-6: Review → Destination →
                    confirmed-apply. The active step jumps once the user
                    picks a destination. */}
                <StepIndicator
                    steps={["Capture", "Review", "Destination", "Apply"]}
                    currentIndex={destId ? 2 : 1}
                    testIdPrefix="apply-step"
                />

                {loading ? (
                    <div className="flex items-center justify-center py-12 text-zinc-400">
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />Loading capture details…
                    </div>
                ) : (
                    <div className="space-y-6">
                        {/* Apps section */}
                        <section data-testid="review-apps-section">
                            <div className="flex items-center justify-between mb-2">
                                <h4 className="text-sm font-semibold text-zinc-200 flex items-center gap-2">
                                    <Package className="w-4 h-4" /> Apps to reinstall
                                </h4>
                                <span className="text-xs text-zinc-400" data-testid="review-apps-count">
                                    {selected.size} of {apps?.length || 0} selected
                                    {visibleApps.length !== (apps?.length || 0) && (
                                        <> · {visibleApps.length} visible</>
                                    )}
                                </span>
                            </div>

                            {/* Search + bulk controls. Bulk ops scope to the
                                currently-visible (filtered) rows so users
                                don't accidentally toggle hidden items. */}
                            <div className="flex items-center gap-2 mb-2">
                                <div className="relative flex-1">
                                    <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none" />
                                    <Input
                                        type="text"
                                        placeholder="Search by name, publisher, or winget ID…"
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                        className="pl-8 h-9 bg-zinc-950/50"
                                        data-testid="review-apps-search"
                                    />
                                </div>
                                <Button
                                    variant="outline" size="sm"
                                    onClick={toggleSelectAllVisible}
                                    disabled={visibleMappableIds.length === 0}
                                    data-testid="review-apps-select-all-btn"
                                    title={allVisibleSelected ? "Deselect all visible" : "Select all visible"}
                                >
                                    {allVisibleSelected
                                        ? <><Square className="w-4 h-4 mr-1" />Deselect</>
                                        : <><CheckSquare className="w-4 h-4 mr-1" />Select all</>}
                                </Button>
                                <Button
                                    variant={showOnlySelected ? "default" : "outline"} size="sm"
                                    onClick={() => setShowOnlySelected(s => !s)}
                                    data-testid="review-apps-only-selected-btn"
                                    title="Show only selected apps"
                                >
                                    Only selected
                                </Button>
                            </div>

                            <div className="rounded-md border border-zinc-800 max-h-64 overflow-y-auto">
                                {visibleApps.map(app => {
                                    const key = installKey(app);
                                    return (
                                    <label
                                        key={`${app.name}-${app.version}`}
                                        className="flex items-center gap-3 p-2 hover:bg-zinc-900/50 border-b border-zinc-800 last:border-0 cursor-pointer"
                                        data-testid={`review-app-${key || app.name}`}
                                    >
                                        <Checkbox
                                            checked={key ? selected.has(key) : false}
                                            disabled={!key}
                                            onCheckedChange={() => key && toggle(key)}
                                        />
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm text-zinc-200 truncate">{app.name}</div>
                                            <div className="text-xs text-zinc-500 truncate">
                                                {app.version || "—"}{app.publisher ? ` · ${app.publisher}` : ""}
                                                {app.winget_id && app.winget_source === "msstore-list"
                                                    ? <span className="text-indigo-400"> · Microsoft Store: {app.winget_id}</span>
                                                    : app.winget_id
                                                    ? <span className="text-emerald-500"> · winget: {app.winget_id}</span>
                                                    : app.msi_product_code && app.msi_cache_captured
                                                    ? <span className="text-sky-400"> · MSI cache captured</span>
                                                    : app.portable_cache_captured
                                                    ? <span className="text-teal-400"> · portable folder captured</span>
                                                    : <CategoryBadge app={app} />}
                                            </div>
                                        </div>
                                        {app.estimated_size_bytes > 0 && (
                                            <span className="text-xs text-zinc-500">{formatBytes(app.estimated_size_bytes)}</span>
                                        )}
                                    </label>
                                    );
                                })}
                                {visibleApps.length === 0 && (
                                    <div className="p-4 text-sm text-zinc-500 text-center">
                                        {search || showOnlySelected
                                            ? "No apps match the current filter."
                                            : "No apps captured."}
                                    </div>
                                )}
                            </div>
                        </section>

                        {/* Settings preview */}
                        {settings && (
                            <section data-testid="review-settings-section">
                                <h4 className="text-sm font-semibold text-zinc-200 mb-2 flex items-center gap-2">
                                    <Settings2 className="w-4 h-4" /> Captured settings preview
                                </h4>
                                <div className="grid grid-cols-2 gap-3 text-xs">
                                    <SummaryTile label="Power scheme" value={settings.power_scheme_path ? "Captured" : "—"} />
                                    <SummaryTile label="Wi-Fi profiles" value={(settings.wifi_profile_files || []).length} />
                                    <SummaryTile label="Printers" value={(settings.printers || []).length} />
                                    <SummaryTile label="Network adapters" value={(settings.network_adapters || []).length} />
                                </div>
                            </section>
                        )}

                        {/* User profile estimate + selection. v2.0: ZIP per
                            top-level folder via existing artifact pipeline.
                            Folders >5 GB are flagged but the operator can
                            still queue them — capture will mark them
                            skipped with size detail. */}
                        {capture.profile_estimate && (
                            <ProfileEstimateSection
                                estimate={capture.profile_estimate}
                                captureId={capture.id}
                                sourceAgentId={capture.source_agent_id}
                                alreadyQueued={!!capture.phases?.["capture-profile"]}
                                onCaptureQueued={onApplied}
                            />
                        )}

                        {/* Artifacts (download) */}
                        <section data-testid="review-artifacts-section">
                            <h4 className="text-sm font-semibold text-zinc-200 mb-2 flex items-center gap-2">
                                <Download className="w-4 h-4" /> Raw artifacts ({(capture.artifacts || []).length})
                            </h4>
                            <div className="space-y-1 text-xs">
                                {(capture.artifacts || []).map(a => (
                                    <a key={a.asset}
                                        href={`/api/migrations/${capture.id}/artifact/${encodeURIComponent(a.asset)}`}
                                        className="flex items-center justify-between p-2 rounded bg-zinc-950/50 hover:bg-zinc-900/50 text-zinc-300"
                                        data-testid={`review-artifact-${a.asset}`}
                                    >
                                        <span className="font-mono">{a.asset}</span>
                                        <span className="text-zinc-500">{formatBytes(a.size_bytes)}</span>
                                    </a>
                                ))}
                            </div>
                        </section>

                        {/* Apply config */}
                        <section className="pt-2 border-t border-zinc-800" data-testid="review-apply-config">
                            <h4 className="text-sm font-semibold text-zinc-200 mb-3">Apply to destination</h4>
                            <div className="space-y-3">
                                <div>
                                    <label className="text-sm text-zinc-300">Destination Agent</label>
                                    <Select value={destId} onValueChange={setDestId}>
                                        <SelectTrigger className="mt-1.5" data-testid="apply-dest-select">
                                            <SelectValue
                                                placeholder={
                                                    otherAgents.length === 0
                                                        ? "No other agents available"
                                                        : `Select a ${captureOs ? osBadge(captureOs).label + " " : ""}destination`
                                                }
                                            />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {otherAgents.map(a => {
                                                const aOs = normalizeOs(a.os);
                                                const meta = osBadge(aOs);
                                                const mismatch = !!(captureOs && aOs && captureOs !== aOs);
                                                return (
                                                    <SelectItem key={a.id} value={a.id} data-testid={`apply-dest-option-${a.id}`}>
                                                        <span className="inline-flex items-center gap-2">
                                                            <span
                                                                className="inline-block h-2 w-2 rounded-full"
                                                                style={{ backgroundColor: meta.dot }}
                                                                aria-hidden
                                                            />
                                                            <span className="font-mono text-[10px] uppercase tracking-wider text-zinc-400">
                                                                {meta.short}
                                                            </span>
                                                            <span>{a.name || a.hostname || a.id.slice(0, 8)}</span>
                                                            <span className="text-zinc-500">— {a.status}</span>
                                                            {mismatch && (
                                                                <span
                                                                    className="ml-1 inline-flex items-center gap-1 rounded bg-amber-500/10 px-1.5 py-0.5 text-[9px] uppercase tracking-wider text-amber-300 border border-amber-500/30"
                                                                    data-testid={`apply-dest-mismatch-${a.id}`}
                                                                >
                                                                    <AlertTriangle size={9} /> OS mismatch
                                                                </span>
                                                            )}
                                                        </span>
                                                    </SelectItem>
                                                );
                                            })}
                                        </SelectContent>
                                    </Select>
                                    {osMismatch && (
                                        <div
                                            className="mt-2 flex items-start gap-2 rounded border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-xs text-amber-200"
                                            data-testid="apply-os-mismatch-warning"
                                        >
                                            <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                                            <div>
                                                <div className="font-semibold">
                                                    OS mismatch — capture is {osBadge(captureOs).label}, destination is {osBadge(destOs).label}.
                                                </div>
                                                <div className="mt-0.5 text-amber-200/70">
                                                    Different package managers and profile layouts: registry import / winget / settings phases will likely fail or be skipped.
                                                    You'll be asked to confirm before Apply runs.
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                                <label className="flex items-center gap-3 cursor-pointer">
                                    <Checkbox checked={includeReg} onCheckedChange={setIncludeReg} data-testid="apply-include-registry" />
                                    <span className="text-sm text-zinc-200">Import registry on destination</span>
                                </label>
                                <label className="flex items-center gap-3 cursor-pointer">
                                    <Checkbox checked={includeSet} onCheckedChange={setIncludeSet} data-testid="apply-include-settings" />
                                    <span className="text-sm text-zinc-200">Apply settings (power, Wi-Fi)</span>
                                </label>
                                {hasProfileCapture && (
                                    <label className="flex items-center gap-3 cursor-pointer">
                                        <Checkbox checked={includeProfile} onCheckedChange={setIncludeProfile} data-testid="apply-include-profile" />
                                        <span className="text-sm text-zinc-200">
                                            Restore user profile
                                            <span className="ml-2 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                                                v2.0
                                            </span>
                                        </span>
                                    </label>
                                )}
                            </div>
                        </section>
                    </div>
                )}

                <DialogFooter>
                    <Button variant="ghost" onClick={onClose} data-testid="apply-cancel-btn">Cancel</Button>
                    <Button onClick={() => submit(false)} disabled={applying || !destId || loading} data-testid="apply-start-btn">
                        {applying ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
                        Apply to Destination
                    </Button>
                </DialogFooter>
            </DialogContent>

            {/* Soft-override confirm dialog for OS-mismatched destinations.
                Operator must explicitly acknowledge that registry / winget /
                profile layout phases will fail or be skipped before we POST. */}
            <Dialog open={confirmMismatch} onOpenChange={(o) => !o && setConfirmMismatch(false)}>
                <DialogContent className="max-w-md" data-testid="apply-os-mismatch-confirm-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-amber-300">
                            <AlertTriangle size={18} /> Confirm OS-mismatched apply
                        </DialogTitle>
                        <DialogDescription>
                            You're applying a <strong>{osBadge(captureOs).label}</strong> capture to a{" "}
                            <strong>{osBadge(destOs).label}</strong> destination. This is unusual and most
                            phases will fail or be silently skipped:
                        </DialogDescription>
                    </DialogHeader>
                    <ul className="ml-2 mt-2 space-y-1 text-xs text-zinc-300 list-disc list-inside">
                        <li><strong>Apps</strong> — package IDs differ across package managers (winget vs apt vs brew).</li>
                        <li><strong>Registry</strong> — Windows-only; will be skipped on Linux/macOS.</li>
                        <li><strong>Profile</strong> — folder layouts differ (e.g. <code>AppData\Roaming</code> has no Linux/macOS equivalent).</li>
                    </ul>
                    <p className="mt-3 text-xs text-zinc-400">
                        Continue anyway only if this is an intentional cross-platform recovery scenario.
                    </p>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setConfirmMismatch(false)} data-testid="apply-os-mismatch-cancel-btn">
                            Cancel
                        </Button>
                        <Button
                            onClick={() => submit(true)}
                            disabled={applying}
                            data-testid="apply-os-mismatch-confirm-btn"
                            className="bg-amber-500 hover:bg-amber-600 text-zinc-950"
                        >
                            {applying ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <AlertTriangle className="w-4 h-4 mr-2" />}
                            Apply anyway
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </Dialog>
    );
}

/**
 * Inline category badge for the app list when the row has no winget_id.
 * Reads the new server-side category fields (`category`, `category_label`)
 * added by the multi-source resolver in `services/app_resolver.py`. Falls
 * back to the legacy amber "manual review required" pill ONLY for old captures
 * that pre-date the resolver (so historic UI never goes blank).
 */
function CategoryBadge({ app }) {
    const cat = app?.category;
    const label = app?.category_label;
    if (!cat) {
        return <span className="text-amber-500"> · manual review required</span>;
    }
    const toneByCategory = {
        custom:   "text-violet-400",
        msi:      "text-sky-400",
        store:    "text-indigo-400",
        portable: "text-teal-400",
        manual:   "text-amber-500",
        skipped:  "text-rose-400",
    };
    const tone = toneByCategory[cat] || "text-amber-500";
    return <span className={tone}> · {label || cat}</span>;
}

function SummaryTile({ label, value }) {    return (
        <div className="p-3 rounded-md bg-zinc-950/50 border border-zinc-800">
            <div className="text-zinc-500">{label}</div>
            <div className="text-zinc-100 text-base font-medium mt-0.5">{value}</div>
        </div>
    );
}

/**
 * User-profile capture section — renders when the agent has reported a
 * ``profile_estimate`` for the capture. Operator ticks the folders
 * they want to migrate, hits "Capture selected folders", and the
 * backend queues a ``migrate-capture-profile`` command on the source
 * agent. Folders that exceed v2.0's 5 GB single-archive cap are
 * flagged with an inline warning but still selectable — the agent
 * will mark them ``skipped`` in the manifest and the dashboard
 * surfaces that detail post-capture.
 */
function ProfileEstimateSection({ estimate, captureId, sourceAgentId, alreadyQueued, onCaptureQueued }) {
    const totalGB     = estimate.total_bytes    / (1024 ** 3);
    const excludedGB  = estimate.excluded_bytes / (1024 ** 3);
    const folders     = estimate.folders || [];
    const presentOnly = folders.filter(f => f.present);
    const fileCount   = presentOnly.reduce((a, f) => a + (f.file_count || 0), 0);
    const CAP_BYTES   = 5 * 1024 * 1024 * 1024;

    // Default-select Desktop / Documents / Favorites / Roaming because
    // those are the high-signal folders most operators always want.
    // Folders larger than the single-archive threshold ship as multi-part
    // chunked archives — never skipped — so size doesn't affect default
    // selection any more (was a v2.0 ZIP-cap leftover).
    const [selected, setSelected] = useState(() => {
        const init = new Set();
        for (const f of presentOnly) {
            const wantedByDefault = ["Desktop", "Documents", "Favorites", "AppData/Roaming"].includes(f.label);
            if (wantedByDefault) init.add(f.rel_path);
        }
        return init;
    });
    const [submitting, setSubmitting] = useState(false);

    const toggle = (rel) => {
        setSelected(prev => {
            const next = new Set(prev);
            if (next.has(rel)) next.delete(rel); else next.add(rel);
            return next;
        });
    };

    const queueCapture = async () => {
        if (selected.size === 0) { toast.error("Pick at least one folder"); return; }
        if (!captureId) { toast.error("Capture id missing"); return; }
        setSubmitting(true);
        try {
            // Append `capture-profile` to the existing capture row instead
            // of spawning a brand-new capture job. Keeps everything for one
            // source PC under a single dashboard row (apps + registry +
            // settings + estimate + capture-profile).
            const { data } = await http.post(`/migrations/${captureId}/capture-profile`, {
                selected_profile_folders: Array.from(selected),
            });
            toast.success(`Profile capture queued (${selected.size} folder(s))`);
            onCaptureQueued?.(data.id);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to queue profile capture");
        } finally {
            setSubmitting(false);
        }
    };

    const selectedBytes = presentOnly
        .filter(f => selected.has(f.rel_path))
        .reduce((a, f) => a + f.size_bytes, 0);
    const selectedGB = selectedBytes / (1024 ** 3);

    return (
        <section data-testid="review-profile-estimate-section">
            <h4 className="text-sm font-semibold text-zinc-200 mb-2 flex items-center gap-2">
                <HardDrive className="w-4 h-4" /> User profile
                <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                    multi-part — large folders ship as chunked archives
                </span>
            </h4>
            {estimate.note && (
                <div className="mb-2 text-xs text-amber-400" data-testid="profile-estimate-note">{estimate.note}</div>
            )}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs mb-3">
                <SummaryTile label="Profile dir" value={estimate.profile_dir || "—"} />
                <SummaryTile label="Total scanned" value={totalGB.toFixed(2) + " GB"} />
                <SummaryTile label="Excluded as caches" value={excludedGB.toFixed(2) + " GB"} />
                <SummaryTile label="Selected to migrate" value={selectedGB.toFixed(2) + " GB"} />
            </div>
            <div className="space-y-1 mb-3">
                {presentOnly.length === 0 && (
                    <div className="text-xs text-zinc-500 p-3 rounded bg-zinc-950/50 border border-zinc-800">
                        No qualifying folders found in the user profile.
                    </div>
                )}
                {presentOnly.map(f => {
                    const gb = f.size_bytes / (1024 ** 3);
                    const oversized = f.size_bytes > CAP_BYTES;
                    const isOn = selected.has(f.rel_path);
                    return (
                        <label key={f.rel_path}
                            className={`flex items-center gap-3 p-2 rounded border cursor-pointer transition-colors ${
                                isOn ? "bg-emerald-950/30 border-emerald-800/60"
                                     : "bg-zinc-950/40 border-zinc-800/60 hover:border-zinc-700"}`}
                            data-testid={`profile-folder-${f.rel_path.replace(/[\\/]/g, '-')}`}>
                            <Checkbox
                                checked={isOn}
                                onCheckedChange={() => toggle(f.rel_path)}
                                data-testid={`profile-folder-cb-${f.rel_path.replace(/[\\/]/g, '-')}`} />
                            <FolderOpen className={`w-4 h-4 shrink-0 ${oversized ? "text-sky-500" : "text-zinc-500"}`} />
                            <span className="flex-1 text-zinc-200">{f.label}</span>
                            {oversized && (
                                <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-sky-500/10 text-sky-400 border border-sky-500/20">
                                    {Math.ceil(f.size_bytes / CAP_BYTES)} parts
                                </span>
                            )}
                            <span className="text-zinc-500 font-mono text-[11px]">{f.file_count.toLocaleString()} files</span>
                            <span className="text-zinc-300 font-mono text-[11px] tabular-nums w-20 text-right">{gb.toFixed(2)} GB</span>
                        </label>
                    );
                })}
            </div>
            <div className="flex items-center justify-between">
                <div className="text-[11px] text-zinc-500">
                    {alreadyQueued
                        ? "A profile capture is already queued for this migration."
                        : "Queues a fresh capture so the destination can apply the selected folders."}
                </div>
                <Button
                    size="sm"
                    onClick={queueCapture}
                    disabled={submitting || selected.size === 0 || alreadyQueued}
                    data-testid="profile-capture-queue-btn"
                    className="bg-emerald-600 hover:bg-emerald-500">
                    {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Camera className="w-3.5 h-3.5 mr-1.5" />}
                    Capture selected folders
                </Button>
            </div>
        </section>
    );
}

